import {FC, FormEventHandler} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import Link from 'next/link'
import {useRouter} from 'next/router'

import RequiredTip from 'components/blocks/RequiredTip'
import Button from 'components/elements/Button'
import Checkbox from 'components/elements/fields/Checkbox'
import MobxDragFile from 'components/elements/fields/MobxDragFile'
import MobxSelect from 'components/elements/fields/MobxSelect'
import MobxTextArea from 'components/elements/fields/MobxTextArea'
import MobxTextInput from 'components/elements/fields/MobxTextInput'
import {Col, Row} from 'components/elements/grid'
import MessageBlock from 'components/elements/MessageBlock'
import usStates from 'src/configs/usStates'
import {posthogTrack, klaviyoTrack} from 'src/utils'
import {useAuthStore, useDraftStore, useRootStore} from 'src/utils/contexts/RootStore.context'
import useAsyncFlow from 'src/utils/hooks/useAsyncFlow'

import s from './FormInvoice.module.scss'
import InvoiceChooseTemplate from './InvoiceChooseTemplate'
import FieldsInvoiceTable from '../FieldsInvoiceTable'
import FieldsInvoiceTax from '../FieldsInvoiceTax'
import FieldsInvoiceTerms from '../FieldsInvoiceTerms'
import useChooseTemplate from '../hooks/useChooseTemplate'

const cx = cn.bind(s)

const invoiceTemplates: IPayStubTemplates[] = [
	{
		id: 1,
		isActive: true,
	},
	{
		id: 2,
		isActive: false,
		theme: 'gray',
	},
	{
		id: 3,
		isActive: false,
		theme: 'gray',
	},
	{
		id: 4,
		isActive: false,
		theme: 'gray',
	},
	{
		id: 5,
		isActive: false,
		theme: 'gray',
	},
	{
		id: 6,
		isActive: false,
		theme: 'gray',
	},
]

interface IFormInvoiceProps {
	className?: string
}

const FormInvoice: FC<IFormInvoiceProps> = ({className}) => {
	const {templates, activeTemplate, handleActiveTemplate, handleTemplateTheme} =
		useChooseTemplate(invoiceTemplates)
	const store = useRootStore()
	const authStore = useAuthStore()
	const draftStore = useDraftStore()
	const router = useRouter()
	const [saveDraftFlow, {isLoading, errorMessage}] = useAsyncFlow<typeof store.invoice.saveDraft>(
		store.invoice.saveDraft,
	)

	const onCheckout = async (e: Parameters<FormEventHandler<HTMLFormElement>>[0]) => {
		e.preventDefault()
		posthogTrack('[FREE INVOICE] Checkout button clicked')
		klaviyoTrack('Checkout clicked', {
			documentType: 'free-invoice',
			hasActiveSubscription: authStore.hasActiveSubscription,
			subscriptionType: authStore.user?.subscription?.subscriptionType,
		})
		const draftId =
			draftStore.type === 'invoice' && draftStore.draftId ? draftStore.draftId : undefined
		const response = await saveDraftFlow(activeTemplate.id, draftId, activeTemplate.theme)()

		if (response?.data.ok) {
			draftStore.setDraft(
				response?.data.data.draftId,
				'invoice',
				activeTemplate.id,
				activeTemplate.theme,
			)

			if (!authStore.isLoggedIn) {
				return router.push('/auth/sign-up')
			}

			const canDownload = authStore.isLoggedIn && authStore.hasActiveSubscription

			router.push({
				pathname: canDownload ? '/download' : '/pricing',
			})
		}
	}

	return (
		<form onSubmit={(e) => onCheckout(e)} className={cx('wrapper', className)}>
			<Col xl={{offset: 1, span: 10}}>
				<InvoiceChooseTemplate
					className={cx('step', 'top-template')}
					{...{templates, activeTemplate, handleActiveTemplate, handleTemplateTheme}}>
					<header className={cx('header')}>
						<h2 className={cx('step__title')}>1. Choose your template</h2>
					</header>
				</InvoiceChooseTemplate>
			</Col>

			<header className={cx('header')}>
				<h2 className={cx('header__title', 'step__title')}>2. Fill out the form</h2>
			</header>
			<RequiredTip />
			<div className={cx('step')}>
				<div className={cx('columns', 'columns--first')}>
					<Row>
						<Col md={6} lg={{offset: 1, span: 5}} xl={{offset: 2, span: 4}}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'Form name'}
								placeholder="Invoice"
								store={store.invoice}
								name="formName"
							/>
						</Col>
						<Col md={6} lg={{span: 5}} xl={{span: 4}}>
							<MobxDragFile
								name="logo"
								store={store.invoice}
								className={cx('logo-field', 'field')}
								label="Logo"
							/>
						</Col>
					</Row>
				</div>
				<div className={cx('columns', 'columns--from-to', 'from-to')}>
					<Row>
						<Col md={6} lg={{offset: 1, span: 5}} xl={{offset: 2, span: 4}} data-testid="from">
							<p className={cx('fs-tab', 'from-to__title')}>From</p>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Name"
								placeholder="Write business name"
								store={store.invoice.from}
								name="name"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Email"
								placeholder="Write business email"
								store={store.invoice.from}
								name="email"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Address 1"
								placeholder="Write street address 1"
								store={store.invoice.from}
								name="address1"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Address 2"
								placeholder="Write street address 2"
								store={store.invoice.from}
								name="address2"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="City"
								placeholder="Write city"
								store={store.invoice.from}
								name="city"
							/>
							<MobxSelect
								wrapperClassName={cx('field')}
								name="state"
								placeholder="Select state"
								store={store.invoice.from}
								label="State"
								data-testid="stateFrom"
								options={usStates}
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Zip"
								placeholder="Write ZIP code"
								store={store.invoice.from}
								name="zip"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Phone"
								placeholder="(123) 456-789"
								store={store.invoice.from}
								name="phone"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Business number"
								placeholder="123-45-6789"
								store={store.invoice.from}
								name="businessNumber"
							/>
						</Col>
						<Col md={6} lg={{span: 5}} xl={{span: 4}} data-testid="to">
							<p className={cx('fs-tab', 'from-to__title', 'from-to__title--to')}>Bill to</p>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Name"
								placeholder="Write client name"
								store={store.invoice.to}
								name="name"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Email"
								placeholder="Write client email"
								store={store.invoice.to}
								name="email"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Address 1"
								placeholder="Write street address 1"
								store={store.invoice.to}
								name="address1"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Address 2"
								placeholder="Write street address 2"
								store={store.invoice.to}
								name="address2"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="City"
								placeholder="Write city"
								store={store.invoice.to}
								name="city"
							/>

							<MobxSelect
								wrapperClassName={cx('field')}
								placeholder="Select state"
								name="state"
								label="State"
								data-testid="stateTo"
								store={store.invoice.to}
								options={usStates}
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Zip"
								placeholder="Write ZIP code"
								store={store.invoice.to}
								name="zip"
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label="Phone"
								placeholder="(123) 456-789"
								store={store.invoice.to}
								name="phone"
							/>
						</Col>
						<Col md={12} lg={{offset: 1, span: 10}} xl={{offset: 2, span: 8}}>
							<hr />
						</Col>
					</Row>
				</div>
				<div className={cx('columns', 'columns--terms-tax')}>
					<Row>
						<Col md={6} lg={{offset: 1, span: 5}} xl={{offset: 2, span: 4}}>
							<FieldsInvoiceTerms className={cx('fields-terms')} />
						</Col>
						<Col md={6} lg={{span: 5}} xl={{span: 4}}>
							<FieldsInvoiceTax className={cx('fields-tax')} />
						</Col>
					</Row>
				</div>
				<Row>
					<FieldsInvoiceTable />
				</Row>
				<Row>
					<Col md={12} lg={{offset: 0, span: 11}} xl={{offset: 0, span: 10}}>
						<div className={cx('total-block')}>
							<div className={cx('total-block__inner')}>
								<ul className={cx('total-block__list')}>
									<li className={cx('total-block__item')}>
										<strong className={cx('total-block__label')}>Subtotal</strong>
										<span className={cx('total-block__value')} data-testid="subtotal">
											{store.invoice.subtotal}
										</span>
									</li>

									{store.invoice.discountType !== 'none' && (
										<li className={cx('total-block__item')}>
											<strong className={cx('total-block__label')}>
												Discount
												{store.invoice.discountType === 'percent' && (
													<span>({store.invoice.discountRate}%)</span>
												)}
											</strong>
											<span className={cx('total-block__value')}>
												<span>-$</span>
												{store.invoice.discount.toFixed(2)}
											</span>
										</li>
									)}

									<li className={cx('total-block__item')}>
										<strong className={cx('total-block__label')}>
											Tax({store.invoice.taxRate}%)
										</strong>
										<span className={cx('total-block__value')} data-testid="tax">
											{store.invoice.tax < 0 ? (
												<>
													<span>- $</span> <span>{Math.abs(store.invoice.tax).toFixed(2)}</span>
												</>
											) : (
												<>
													<span>+ $</span> <span>{Math.abs(store.invoice.tax).toFixed(2)}</span>
												</>
											)}
										</span>
									</li>
									<li className={cx('total-block__item')}>
										<strong className={cx('total-block__label')}>Total</strong>
										<span className={cx('total-block__value')} data-testid="total">
											$ {store.invoice.total.toFixed(2)}
										</span>
									</li>
									<li className={cx('total-block__item')}>
										<strong className={cx('total-block__label')}>Balance due</strong>
										<span className={cx('total-block__value')}>
											$ {store.invoice.total.toFixed(2)}
										</span>
									</li>
								</ul>
							</div>
						</div>
					</Col>
				</Row>
				<Row>
					<Col md={12} lg={{offset: 1, span: 10}} xl={{offset: 2, span: 8}}>
						<MobxTextArea
							wrapperClassName={cx('notes')}
							className={cx('notes__inner')}
							name="notes"
							store={store.invoice}
							label="Notes"
							placeholder="Any relevant information not covered, additional terms and conditions"
						/>
					</Col>
				</Row>
			</div>

			<Row>
				<Col xl={{offset: 1, span: 10}}>
					<InvoiceChooseTemplate
						className="top-template"
						{...{
							templates,
							activeTemplate,
							handleActiveTemplate,
							handleTemplateTheme,
							isPreview: true,
						}}>
						<header className={cx('header')}>
							<h2 className={cx('step__title')}>3. Review your final invoice</h2>
						</header>
					</InvoiceChooseTemplate>
				</Col>
			</Row>

			<Row>
				<Col md={{offset: 2, span: 8}} lg={{offset: 3, span: 6}} xl={{offset: 3, span: 6}}>
					<div className={cx('step', 'step--finish', 'finish')}>
						<div className={cx('finish__content')}>
							<div className={cx('finish__fields')}>
								<Checkbox
									required
									name="privacy"
									value={undefined}
									label="I agree that I will not use this site for any illegal purposes"
								/>
								<Checkbox
									required
									value={undefined}
									name="terms"
									label={
										<span>
											Please accept our <Link href="/terms">Terms of Use</Link> and
											<Link href="/privacy-policy">Privacy Policy</Link>{' '}
										</span>
									}
								/>
							</div>
							{errorMessage && <MessageBlock type="error" message={errorMessage} />}
							<div className={cx('finish__buttons')}>
								<Button type="submit" isLoading={isLoading} size="big" data-testid="checkout">
									Checkout
								</Button>
							</div>
						</div>
					</div>
				</Col>
			</Row>
		</form>
	)
}

FormInvoice.displayName = 'FormInvoice'

export default observer(FormInvoice)
